import { useEffect } from 'react';
import type { ToastType } from '../types';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
}

// SVG Icons
const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const AlertTriangleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const BellIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
  </svg>
);

/**
 * Toast Notification Component
 * @param message - Message à afficher
 * @param type - Type de toast ('success', 'error', 'warning', 'info')
 * @param duration - Durée d'affichage en ms (default: 3000)
 * @param onClose - Callback appelé à la fermeture
 */
export function Toast({ message, type = 'info', duration = 3000, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onClose) onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <AlertCircleIcon />;
      case 'warning':
        return <AlertTriangleIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <BellIcon />;
    }
  };

  return (
    <div
      className={`toast toast-${type}`}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={{
        opacity: 1,
        transform: 'translateX(0)',
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      <div className="toast-icon" aria-hidden="true">{getIcon()}</div>
      <span className="toast-message">{message}</span>
    </div>
  );
}

// Types for toast options
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  showProgress?: boolean;
}

// Toast API return type with convenience methods
export interface ToastAPI {
  (message: string, type?: ToastType, duration?: number, options?: ToastOptions): void;
  success: (message: string, duration?: number, options?: ToastOptions) => void;
  error: (message: string, duration?: number, options?: ToastOptions) => void;
  warning: (message: string, duration?: number, options?: ToastOptions) => void;
  info: (message: string, duration?: number, options?: ToastOptions) => void;
}

/**
 * Hook pour afficher des toasts
 * @returns toast function with convenience methods (success, error, warning, info)
 */
export function useToast(): ToastAPI {
  const toast = (
    message: string,
    type: ToastType = 'info',
    duration: number = 3000,
    options?: ToastOptions
  ): void => {
    // Supprimer les toasts existants si trop nombreux
    const existingToasts = document.querySelectorAll('.toast');
    if (existingToasts.length > 5) {
      const oldestToast = existingToasts[0] as HTMLElement;
      oldestToast.style.opacity = '0';
      oldestToast.style.transform = 'translateX(120%)';
      setTimeout(() => oldestToast.remove(), 300);
    }

    // Calculer la position verticale en fonction des toasts existants
    let topPosition = 20; // Position initiale
    const toastGap = 10; // Espacement entre les toasts

    existingToasts.forEach((existingToast) => {
      const toastHeight = (existingToast as HTMLElement).offsetHeight || 70;
      topPosition += toastHeight + toastGap;
    });

    // Créer le toast
    const toastEl = document.createElement('div');
    toastEl.className = `toast toast-${type}`;
    toastEl.style.top = `${topPosition}px`;
    toastEl.style.position = 'fixed';

    const icons: Record<ToastType, string> = {
      success: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`,
      error: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`,
      warning: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`,
      info: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`
    };

    const icon = icons[type] || icons.info;

    // 🔒 SECURITY FIX: Build DOM structure safely to prevent XSS
    // Instead of innerHTML with user input, create elements programmatically

    // Set ARIA attributes for accessibility
    toastEl.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toastEl.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    toastEl.setAttribute('aria-atomic', 'true');

    // Create content wrapper
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'toast-content-wrapper';

    // Create icon element
    const iconEl = document.createElement('div');
    iconEl.className = 'toast-icon';
    iconEl.setAttribute('aria-hidden', 'true'); // Hide decorative icon from screen readers
    iconEl.innerHTML = icon; // Safe: icon is from our trusted icons object
    contentWrapper.appendChild(iconEl);

    // Create message element - SAFE: uses textContent instead of innerHTML
    const messageEl = document.createElement('span');
    messageEl.className = 'toast-message';
    messageEl.textContent = message; // ✅ SAFE: textContent escapes HTML automatically
    contentWrapper.appendChild(messageEl);

    // Add action button if provided
    if (options?.action) {
      const actionButton = document.createElement('button');
      actionButton.className = 'toast-action-button';
      actionButton.textContent = options.action.label; // ✅ SAFE: textContent
      actionButton.dataset.action = 'true';
      contentWrapper.appendChild(actionButton);
    }

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'toast-close-button';
    closeButton.setAttribute('aria-label', 'Fermer la notification');
    closeButton.dataset.close = 'true';
    closeButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>`; // Safe: hardcoded SVG
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      removeToast(toastEl);
    });
    contentWrapper.appendChild(closeButton);

    toastEl.appendChild(contentWrapper);

    // Add progress bar if enabled
    if (options?.showProgress !== false) {
      const progressBar = document.createElement('div');
      progressBar.className = 'toast-progress-bar';
      const progressFill = document.createElement('div');
      progressFill.className = 'toast-progress-fill';
      progressFill.style.width = '100%';
      progressFill.style.transition = `width ${duration}ms linear`;
      progressBar.appendChild(progressFill);
      toastEl.appendChild(progressBar);
    }

    document.body.appendChild(toastEl);

    // Start progress bar animation
    if (options?.showProgress !== false) {
      const progressFill = toastEl.querySelector('.toast-progress-fill') as HTMLElement;
      if (progressFill) {
        setTimeout(() => {
          progressFill.style.width = '0%';
        }, 10);
      }
    }

    // Animation d'entrée
    setTimeout(() => {
      toastEl.style.opacity = '1';
      toastEl.style.transform = 'translateX(0)';
    }, 10);

    // Handle action button click
    if (options?.action) {
      const actionButton = toastEl.querySelector('[data-action="true"]');
      if (actionButton) {
        actionButton.addEventListener('click', (e) => {
          e.stopPropagation();
          options.action!.onClick();
          removeToast(toastEl);
        });
      }
    }

    // Durée selon le type
    const toastDuration = type === 'warning' ? 5000 : duration;

    // Retirer le toast automatiquement
    const removeTimer = setTimeout(() => {
      removeToast(toastEl);
    }, toastDuration);

    // Store the timer ID on the element for cleanup
    (toastEl as any).__removeTimer = removeTimer;
  };

  // Add convenience methods to the toast function
  const toastWithMethods = toast as ToastAPI;
  toastWithMethods.success = (message: string, duration?: number, options?: ToastOptions) =>
    toast(message, 'success', duration, options);
  toastWithMethods.error = (message: string, duration?: number, options?: ToastOptions) =>
    toast(message, 'error', duration, options);
  toastWithMethods.warning = (message: string, duration?: number, options?: ToastOptions) =>
    toast(message, 'warning', duration, options);
  toastWithMethods.info = (message: string, duration?: number, options?: ToastOptions) =>
    toast(message, 'info', duration, options);

  return toastWithMethods;
}

/**
 * Remove toast with animation
 */
function removeToast(toastEl: HTMLElement): void {
  // Clear the auto-remove timer if it exists
  if ((toastEl as any).__removeTimer) {
    clearTimeout((toastEl as any).__removeTimer);
  }

  toastEl.style.opacity = '0';
  toastEl.style.transform = 'translateX(120%)';
  setTimeout(() => {
    toastEl.remove();
    repositionToasts();
  }, 300);
}

/**
 * Réajuste les positions de tous les toasts après la suppression d'un toast
 */
function repositionToasts(): void {
  const toasts = document.querySelectorAll('.toast');
  let topPosition = 20;
  const toastGap = 10;

  toasts.forEach((toastEl) => {
    const toast = toastEl as HTMLElement;
    toast.style.transition = 'top 0.3s ease';
    toast.style.top = `${topPosition}px`;
    const toastHeight = toast.offsetHeight || 70;
    topPosition += toastHeight + toastGap;
  });
}
